import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import './Slider.css';
import { Virtual } from 'swiper/modules';

function Slider(props) {
    const [swiper, setSwiper] = useState(null);
    const videoRefs = useRef([]);
    const [mediaLoaded, setMediaLoaded] = useState({});
    const [loadingErrors, setLoadingErrors] = useState({});
    const [playbackErrors, setPlaybackErrors] = useState({});

    // Memoize updateVideos to prevent it from changing on every render
    const updateVideos = useCallback(() => {
        videoRefs.current.forEach((video, index) => {
            if (!video) return;

            if (props.index !== props.swiperIndex) {
                video.muted = true;
                video.pause();
                return;
            }

            video.muted = props.muted;

            if (index % props.media.length === props.savedSliderIndexes[props.index]) {
                video.play().catch(err => {
                    console.warn('Error playing video:', err);

                    // Track which videos have playback errors
                    if (props.mobile && /Instagram/.test(navigator.userAgent)) {
                        setPlaybackErrors(prev => ({ ...prev, [index]: true }));
                    }
                });
            } else {
                video.pause();
            }
        });
    }, [props.index, props.swiperIndex, props.muted, props.savedSliderIndexes]);

    // Function to retry video playback
    const retryVideoPlayback = (index) => {
        const video = videoRefs.current[index];
        if (!video) return;

        // Clear the playback error for this video
        setPlaybackErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[index];
            return newErrors;
        });

        // Attempt to play the video again
        video.play().catch(err => {
            console.warn('Error playing video on retry:', err);
            setPlaybackErrors(prev => ({ ...prev, [index]: true }));
        });
    };

    useEffect(() => {
        // Pause all videos except the one at the current slider index
        updateVideos();
    }, [props.index, props.muted, props.swiperIndex, updateVideos]);

    // Handle video loading errors
    const handleVideoError = (index, media) => {
        console.error(`Error loading video at index ${index}: ${media}`);
        setLoadingErrors(prev => ({ ...prev, [media]: true }));
    };

    // Handle video loaded successfully
    const handleVideoLoaded = (index, media) => {
        setMediaLoaded(prev => ({ ...prev, [media]: true }));
    };

    const switchSlide = () => {
        if (props.isRight) {
            swiper?.slideNext();
        } else if (props.isLeft) {
            swiper?.slidePrev();
        } else {
            props.switchProjectSlide();
        }
    };

    return (
        <div onClick={props.mobile ? null : switchSlide} className={!props.mobile ? 'custom-swiper-wrapper' : 'custom-swiper-wrapper-mobile'}>
            {!props.showInformation && !props.mobile && (
                <Swiper
                    slidesPerView="5"
                    centeredSlides={true}
                    allowTouchMove={true}
                    speed={500}
                    loop={true}
                    style={{ height: '100%' }}
                    initialSlide={0}
                    onSlideChange={(swiper) => {
                        props.savedSliderIndexes[props.index % props.projectFolderLength] = swiper.realIndex;
                        props.setSavedSliderIndexes([...props.savedSliderIndexes]);
                    }}
                    onSwiper={(swiper) => {
                        setSwiper(swiper);
                        props.addProjectsPagesSwiper(swiper, props.index % props.projectFolderLength);
                        props.savedSliderIndexes[props.index % props.projectFolderLength] = swiper.realIndex;
                        props.setSavedSliderIndexes([...props.savedSliderIndexes]);
                        updateVideos();
                    }}
                >
                    {props.media.map((media, index) => (
                        <SwiperSlide
                            style={{ height: '100%' }}
                            className="slide"
                            key={index}
                        >
                            {media.endsWith('.mp4') ? (
                                <video
                                    className="slide-media"
                                    src={media}
                                    muted
                                    playsInline
                                    loop
                                    ref={(ref) => (videoRefs.current[index] = ref)}
                                    poster={props.posterImage[index]}
                                    onError={() => handleVideoError(index, media)}
                                    onLoadedData={() => handleVideoLoaded(index, media)}
                                />
                            ) : (
                                <img className="slide-media" src={media} alt="Slide" />
                            )}
                        </SwiperSlide>
                    ))}
                </Swiper>
            )
            }
            {!props.showInformation && props.mobile && (
                <Swiper
                    slidesPerView="2"
                    centeredSlides={true}
                    allowTouchMove={true}
                    touchRatio={1}
                    loop={true}
                    style={{ height: '100%', width: '120%', transform: 'translateX(-8.3333%)' }}
                    initialSlide={0}
                    modules={[Virtual]}
                    virtual
                    onSlideChange={(swiper) => {
                        props.savedSliderIndexes[props.index] = swiper.realIndex % props.media.length;
                        props.setSavedSliderIndexes([...props.savedSliderIndexes]);
                    }}
                    onSwiper={(swiper) => {
                        setSwiper(swiper);
                        props.addProjectsPagesSwiper(swiper, props.index);
                        props.savedSliderIndexes[props.index] = swiper.realIndex;
                        props.setSavedSliderIndexes([...props.savedSliderIndexes]);
                        updateVideos();
                    }}
                >
                    {[...props.media, ...props.media, ...props.media].map((media, index) => {
                        const actualIndex = index % props.media.length;
                        const poster = props.posterImage[actualIndex];

                        return (
                            <SwiperSlide
                                style={{ height: '100%' }}
                                className="slide"
                                key={index}
                                virtualIndex={index}
                            >
                                {media.endsWith('.mp4') ? (
                                    <React.Fragment>
                                        {loadingErrors[media] ? (
                                            <img
                                                className="slide-media"
                                                src={poster}
                                                alt="Video Poster"
                                            />
                                        ) : (
                                            <>
                                                <video
                                                    className="slide-media"
                                                    src={media}
                                                    muted
                                                    playsInline
                                                    loop
                                                    ref={(ref) => (videoRefs.current[index] = ref)}
                                                    poster={poster}
                                                    onError={() => handleVideoError(index, media)}
                                                    onLoadedData={() => handleVideoLoaded(index, media)}
                                                    preload="metadata"
                                                />
                                                {playbackErrors[index] && (
                                                    <div
                                                        className="video-overlay"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            retryVideoPlayback(index);
                                                        }}
                                                    >
                                                        <button className="play-button">
                                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M8 5V19L19 12L8 5Z" fill="currentColor" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </React.Fragment>
                                ) : (
                                    <img className="slide-media" src={media} alt="Slide" />
                                )}
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            )
            }
            {props.showInformation && <div className="information-text">{props.information}</div>}
        </div >
    );
}

export default Slider;
